import { Dialog, Transition } from '@headlessui/react';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import TopNavbar from 'layouts/TopBar/TopNavBar';
import { Fragment, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useAppSelector } from '../store/store';
import SidebarNavigation from './SideBar/SidebarNavigation';
import { ROLE_SUPER_ADMIN } from 'shared/permissions/roles';

/**
 * @component
 * Tenant Layout Shell
 *
 * Purpose:
 * Main application layout component providing a unified, high-performance structural shell for
 * the multi-context tenant interface. It enforces a modern SaaS design pattern where the top navigation bar
 * spans full-width, the desktop sidebar remains static, and scrolling is strictly isolated to the page
 * content area. It serves as the parent layout wrapper for all authenticated tenant views.
 *
 * Interface:
 * - No external props: Self-contained component driving structural state and orchestration.
 * - Redux Dependencies: auth.groups (for role assessment).
 * - Route Integration: Mounts a single `<Outlet />` within an isolated scrolling workspace.
 * - State Handlers: Manages local sidebar collapse/mobile drawer toggles.
 *
 * Features & UI Architecture:
 * - Top-Level Orchestration: Enforces a rigid layout via `h-screen overflow-hidden`. The top-bar is
 *   pinned to a fixed height (`h-14`), forcing vertical content expansion into a dedicated container.
 * - Linear Layout Flow: Moves the `TopNavbar` above the `aside` sidebar to ensure a full-width header span,
 *   matching corporate dashboard layouts.
 * - Static Responsive Sidebars:
 *   - Desktop: Width switches smoothly between 280px (expanded) and 20px/80px (collapsed) with an
 *     independent height boundary `h-[calc(100vh-56px)]` to prevent viewport overflow.
 *   - Mobile: Relies on an un-nested, accessible Headless UI Dialog drawer triggered via navbar actions.
 * - Isolated Page Scrolling: Conveys layout scroll control exclusively to the `<main>` tag via
 *   `overflow-y-auto bg-white h-full`. Top Navbar and Sidebars remain perfectly static during user scroll.
 * - Visual Theme Standardization: Discards dynamic color interpolation strings (`bg-${themeColor}-700`)
 *   in favor of a consistent, premium neutral color palette (`#f8f9fa`, `#212529`, and light slate borders).
 * - Component Decoupling: Offloads complex state operations (context switching, polling, profiling,
 *   and breadcrumb tracking) directly into the self-contained `TopNavbar` and `SidebarNavigation` child structures.
 */
const Tenant = () => {
  const groups = useAppSelector((state) => state.auth.groups);
  const themeColor = groups.includes(ROLE_SUPER_ADMIN) ? 'stone' : 'primary';

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <div className="flex flex-col h-screen min-h-screen bg-[#f8f9fa] overflow-hidden">
      {/* -------------------- TOP NAVBAR  -------------------- */}
      <div className="w-full shrink-0 z-50">
        <TopNavbar />
      </div>

      {/* -------------------- AREA SOTTOSTANTE -------------------- */}
      <div className="flex flex-1 min-w-0 h-[calc(100vh-56px)] overflow-hidden">
        {/* -------------------- SIDEBAR DESKTOP  -------------------- */}
        <aside
          id="side-bar"
          className={`
        hidden md:flex flex-col justify-between bg-[#f8f9fa] h-full shrink-0
        border-r border-slate-200/80 transition-all duration-300 ease-in-out z-40
        ${sidebarCollapsed ? 'w-20' : 'w-[280px]'}
      `}>
          {/* -------------------- HEADER SIDEBAR (Logo) -------------------- */}
          <div className="flex items-center justify-between px-5 pt-6 pb-4 shrink-0">
            {/* Expanded Sidebar Header */}
            {!sidebarCollapsed ? (
              <div className="flex items-center gap-3 animate-fade-in text-left">
                <div className="mr-4">
                  <img className="h-8 w-auto" src="/logo_GENESYS.png" alt="Workflow" />
                </div>
              </div>
            ) : (
              // <div className="mx-auto text-xs font-bold text-[#1e3a8a]">LAB</div>
              <div onClick={() => setSidebarCollapsed(false)} className="cursor-pointer">
                <img
                  className="h-8 w-10"
                  src="/logo_blank.png"
                  alt="Workflow"
                  loading="eager"
                  decoding="async"
                />
              </div>
            )}

            {/* Collapse Toggle Button */}
            {!sidebarCollapsed && (
              <IconButton
                onClick={() => setSidebarCollapsed(true)}
                className="text-slate-500 hover:bg-slate-200/50 rounded-full p-1.5 transition-colors">
                <ArrowLeftIcon className="h-5 w-5 stroke-[2.5]" />
              </IconButton>
            )}
          </div>

          {/* -------------------- NAVIGAZIONE MENU -------------------- */}
          <div className="flex-1 overflow-y-auto px-2 py-2 font-sans text-[14px] font-medium text-[#212529] scrollbar-none">
            <SidebarNavigation collapsed={sidebarCollapsed} />
          </div>
        </aside>

        {/* -------------------- CONTENUTO DELLA PAGINA  -------------------- */}
        <main className="h-full flex-1 overflow-y-auto bg-white">
          <div className="mx-auto w-full  px-4 py-4 sm:px-6 sm:py-6 lg:px-8 animate-fade-in">
            <Outlet />
          </div>
        </main>
      </div>

      {/* -------------------- SIDEBAR MOBILE -------------------- */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 md:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0">
            <div className="fixed inset-0 bg-gray-600/75" />
          </Transition.Child>

          <div className="fixed inset-0 flex z-50">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full">
              <Dialog.Panel className="relative flex-1 flex flex-col max-w-xs w-full pt-5 pb-4 bg-white border-r border-slate-200">
                <div className="absolute top-0 right-0 -mr-12 pt-2">
                  <button
                    type="button"
                    className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none ring-2 ring-white"
                    onClick={() => setSidebarOpen(false)}>
                    <span className="sr-only">Close sidebar</span>
                    <CloseIcon className="h-6 w-6 text-white" aria-hidden="true" />
                  </button>
                </div>
                <div className="mt-4 flex-1 h-0 overflow-y-auto px-2">
                  <SidebarNavigation collapsed={false} />
                </div>
              </Dialog.Panel>
            </Transition.Child>
            <div className="flex-shrink-0 w-14" aria-hidden="true"></div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default Tenant;
